/* ----------------------------------------------------------------------------
 * GAP #1 — direct keyword-targeted content generation: thin api glue.
 *
 * The CONTRACT (endpoint paths + request/response shapes) and ALL logic live in
 * content-generate-model.ts (pure, unit-tested). This file ONLY binds the real
 * lib/api client to that model: build request -> POST -> interpret success /
 * map the 409 duplicate + 422 validation. Both modes run the server-side
 * ContentEngine::checkDuplicate before any spend, so a duplicate is refused 409
 * identically for both — there is no dedup-bypass path here by construction.
 * -------------------------------------------------------------------------- */
import { api, ApiError } from '@/lib/api';
import { listConnections } from '@/data/connections';
import type { ConnectionDto } from '@/data/connections-model';
import {
  CONNECTOR_TARGET_INDEX_ENDPOINT,
  CONTENT_GENERATE_ENDPOINTS,
  INDEX_SYNC_ENDPOINT,
  buildKeywordGenerateRequest,
  buildPromptGenerateRequest,
  interpretGenerateSuccess,
  mapGenerateError,
  type GenerateOutcome,
  type GenerateSuccessResponse,
  type SelectableKeyword,
  type TargetIndexResponse,
} from '@/data/content-generate-model';

export type { GenerateOutcome, SelectableKeyword, TargetIndexResponse } from '@/data/content-generate-model';

/** Shared: POST the request, interpret success, or map the known failures. Any
 *  error the model does not claim (network/5xx/other 4xx) is rethrown verbatim
 *  so the surfaces show their honest generic states. */
async function runGenerate(
  endpoint: string,
  body: unknown,
): Promise<GenerateOutcome> {
  try {
    const res = await api.post<GenerateSuccessResponse>(endpoint, body);
    return interpretGenerateSuccess(res);
  } catch (e) {
    if (e instanceof ApiError) {
      const mapped = mapGenerateError({
        status: e.status,
        code: e.code,
        message: e.message,
        payload: e.payload,
      });
      if (mapped) return mapped;
    }
    throw e;
  }
}

/**
 * MODE (i) — generate a blog draft for a SELECTED tracked keyword. Used by both
 * the Keywords/Rankings row-action and the Copilot tracked-keyword picker (the
 * row IS the selection; the picker IS the selection). No free-text parse.
 */
export function generateFromKeyword(
  keyword: SelectableKeyword,
  targetPageUrl?: string | null,
): Promise<GenerateOutcome> {
  const body = buildKeywordGenerateRequest(keyword, targetPageUrl);
  return runGenerate(CONTENT_GENERATE_ENDPOINTS.fromKeyword, body);
}

/**
 * MODE (ii) — generate from a RAW structured prompt. The FE forwards the prompt
 * verbatim; the BE extracts topic / target_page (validated against the site
 * inventory) / location. Renders a ready draft, the 409 duplicate notice, or an
 * honest 422 validation message.
 */
export function generateFromPrompt(
  prompt: string,
  projectId: number | null,
): Promise<GenerateOutcome> {
  const body = buildPromptGenerateRequest(prompt, projectId);
  return runGenerate(CONTENT_GENERATE_ENDPOINTS.fromPrompt, body);
}

/* --- staleness fail-closed: sync-now + one auto-retry (spec 2026-08-04) ----- */

/** What the EXISTING connector manual-sync route returns on 200. */
export interface IndexSyncResult {
  ok: boolean;
  upserted: number;
  considered: number;
}

/**
 * Sync ONE connector target's published-content index via the EXISTING manual
 * route (session + CSRF + workspace-ownership + 409-when-not-runnable are all
 * enforced server-side — this is the per-click surface, NOT the cron core).
 * Errors (503 connector_disabled / 409 not-runnable / 404) throw ApiError
 * verbatim so the surfaces render an honest inline state.
 */
export async function syncTargetIndex(targetId: number): Promise<IndexSyncResult> {
  const res = await api.post<Partial<IndexSyncResult>>(INDEX_SYNC_ENDPOINT(targetId), {});
  return {
    ok: res?.ok !== false,
    upserted: Number(res?.upserted) || 0,
    considered: Number(res?.considered) || 0,
  };
}

/**
 * The fail-closed "Sync now" moment: sync the target index, then re-fire the
 * SAME generate request EXACTLY ONCE (the user already asked to generate; the
 * missing index was the only blocker). Returns the sync result plus the retried
 * outcome. It NEVER loops — a second `inventory_not_synced` (the sync found zero
 * published posts) comes back in `outcome` for the caller to render honestly
 * WITHOUT re-invoking this helper.
 */
export async function syncThenRetryGenerate(
  targetId: number,
  regenerate: () => Promise<GenerateOutcome>,
): Promise<{ sync: IndexSyncResult; outcome: GenerateOutcome }> {
  const sync = await syncTargetIndex(targetId);
  const outcome = await regenerate();
  return { sync, outcome };
}

/* --- target-page picker (option b): connections + the synced content index --- */

/**
 * GET /v1/connector/connections — this workspace's connected sites. Thin
 * delegate to the existing authorised connector fetch (data/connections.ts) so
 * the picker resolves the domain's connector target WITHOUT duplicating the
 * secret-free projection or re-declaring the endpoint.
 */
export function fetchConnections(signal?: AbortSignal): Promise<ConnectionDto[]> {
  return listConnections(signal);
}

/**
 * GET /v1/connector/targets/{targetId}/index[?topic=] — the connected site's
 * synced published-content inventory for the stacked target-page picker.
 * Optional `topic` (the selected keyword text) yields a `suggested_target` for
 * pre-selection. Read-only; session + workspace gated server-side.
 */
export function fetchTargetIndex(
  targetId: number,
  topic?: string | null,
  signal?: AbortSignal,
): Promise<TargetIndexResponse> {
  const t = (topic ?? '').trim();
  return api.get<TargetIndexResponse>(
    CONNECTOR_TARGET_INDEX_ENDPOINT(targetId),
    t !== '' ? { topic: t } : undefined,
    signal,
  );
}
